import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, withModifiers as _withModifiers, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-35ebdc63"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "text-left" }
const _hoisted_2 = { class: "checkbox-container" }
const _hoisted_3 = { class: "checkbox" }
const _hoisted_4 = { for: "MechanicalRights" }
const _hoisted_5 = {
  key: 1,
  class: "text-info"
}
const _hoisted_6 = { class: "checkbox-container" }
const _hoisted_7 = { class: "checkbox" }
const _hoisted_8 = { for: "PerformingRights" }
const _hoisted_9 = {
  key: 3,
  class: "text-info"
}
const _hoisted_10 = { class: "dialog-actions" }
const _hoisted_11 = ["disabled"]

import { ref, computed } from 'vue'
import { useGettext } from 'vue3-gettext'
import { FlattenAndSetPathsType, useForm } from 'vee-validate'
import { object, string, number } from 'yup'
import { useUserStore } from '@/store/user'
import DialogUI from '@/components/CommonUI/DialogUI.vue'
import TextField from '@/components/CommonUI/TextField.vue'
import IconUI from '@/components/CommonUI/IconUI.vue'
import PushButtons from '@/components/CommonUI/PushButtons.vue'
import DropdownUI from '@/components/CommonUI/DropdownUI.vue'
import LoaderUI from '@/components/CommonUI/LoaderUI.vue'
import { AFFILIATED_MROS, AFFILIATED_PROS } from '@/constants/societies'
import { isApiErrorResponse } from '@/helpers/api'


export default /*#__PURE__*/_defineComponent({
  __name: 'UserDetailsConfirmationDialog',
  emits: ["save"],
  setup(__props, { expose: __expose, emit }) {

const { $gettext } = useGettext()
const userStore = useUserStore()

const dialog = ref<IDialogUI>()
const isMemberOfSociety = ref(Boolean(userStore.userDetails?.ipiCaeNumber))
const isMechanicalRights = ref(Boolean(userStore.userDetails?.mro))
const isPerformingRights = ref(Boolean(userStore.userDetails?.pro))
const isLoading = ref(false)

function show() {
  dialog.value?.show()
}
function hide() {
  dialog.value?.hide()
}

__expose({
  show,
  hide,
})



const formSchema = object({
  userDetails: object({
    firstname: string()
      .required($gettext('Please fill required fields.'))
      .min(2, $gettext('Field must be at least 2 characters.'))
      .max(24, $gettext('This field may not be greater than 24 characters.')),
    lastname: string()
      .required($gettext('Please fill required fields.'))
      .min(2, $gettext('Field must be at least 2 characters.'))
      .max(24, $gettext('This field may not be greater than 24 characters.')),
    ipiCaeNumber: string()
      .notRequired()
      .test(
        'is-valid-ipi',
        $gettext('The entered IPI/CAE number is invalid.'),
        value => {
          if (isMemberOfSociety.value) {
            return /^\d{9,11}$/.test(String(value))
          }

          return true
        },
      ),
    mro: number()
      .notRequired()
      .test(
        'is-valid-mro',
        $gettext('Please fill required fields.'),
        value => {
          if (isMemberOfSociety.value && isMechanicalRights.value) {
            return Number.isInteger(value)
          }

          return true
        },
      ),
    pro: number()
      .notRequired()
      .test(
        'is-valid-pro',
        $gettext('Please fill required fields.'),
        value => {
          if (isMemberOfSociety.value && isPerformingRights.value) {
            return Number.isInteger(value)
          }

          return true
        },
      ),
  }),
})

const { handleSubmit, meta, setErrors, values } = useForm({
  validationSchema: formSchema,
  initialValues: {
    userDetails: {
      firstname: userStore.userDetails?.firstname,
      lastname: userStore.userDetails?.lastname,
      ipiCaeNumber: userStore.userDetails?.ipiCaeNumber,
      mro: userStore.userDetails?.mro,
      pro: userStore.userDetails?.pro,
    },
  } as UserDetailsForm,
})

async function updateUserDetails(userDetails: UserDetailsForm) {
  try {
    isLoading.value = true

    await userStore.updateUserDetails(userDetails)

    hide()
    isLoading.value = false
    emit('save')
  } catch (error) {
    if (isApiErrorResponse(error)) {
      const errors = (error as ApiResponseError).errors

      setErrors(errors as Partial<FlattenAndSetPathsType<UserDetailsForm, string | string[] | undefined>>)
    }
    isLoading.value = false
  }
}

const onSubmit = handleSubmit((values: UserDetailsForm) => updateUserDetails(values))

const mechanicalRightsSocietiesOptions = computed(
  () => userStore.mechanicalRightsSocieties?.map<DropdownOption>(({ id, acronym }) => ({ id, name: acronym })),
)
const performingRightsSocietiesOptions = computed(
  () => userStore.performingRightsSocieties?.map<DropdownOption>(({ id, acronym }) => ({ id, name: acronym })),
)
const hasIpiCaeNumber = computed(() => !!userStore.userDetails?.ipiCaeNumber)

const hasSelectedUnaffiliatedMRO = computed(() => {
  const mroAcronym = userStore.mechanicalRightsSocieties?.find(mro => mro.id === values.userDetails.mro)?.acronym

  return (isMechanicalRights.value && mroAcronym && !AFFILIATED_MROS.includes(mroAcronym))
})

const hasSelectedUnaffiliatedPRO = computed(() => {
  const proAcronym = userStore.performingRightsSocieties?.find(pro => pro.id === values.userDetails.pro)?.acronym

  return (isPerformingRights.value && proAcronym && !AFFILIATED_PROS.includes(proAcronym))
})

return (_ctx: any,_cache: any) => {
  const _directive_translate = _resolveDirective("translate")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createBlock(DialogUI, {
    ref_key: "dialog",
    ref: dialog,
    size: "small",
    "data-testid": "user-details-confirmation-dialog",
    title: _unref($gettext)('Are these details correct?')
  }, {
    default: _withCtx(() => [
      _withDirectives(_createVNode(LoaderUI, { "data-testid": "user-details-confirmation-loader" }, null, 512), [
        [_vShow, isLoading.value]
      ]),
      _withDirectives(_createElementVNode("form", {
        class: "dialog-body",
        onSubmit: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("p", null, _toDisplayString(_unref($gettext)('Please make sure the following information is accurate and matches the details on any forms of official ID you currently hold (eg. Driving License, Passport, etc).')), 1),
          _createElementVNode("p", null, _toDisplayString(_unref($gettext)('The information you provide here will be used by Ditto Music Publishing to register your Musical Works with multiple collection societies and royalty sources around the world.')), 1)
        ]),
        _createVNode(TextField, {
          name: "userDetails.firstname",
          label: _unref($gettext)('First name')
        }, null, 8, ["label"]),
        _createVNode(TextField, {
          name: "userDetails.lastname",
          label: _unref($gettext)('Surname')
        }, null, 8, ["label"]),
        _withDirectives((_openBlock(), _createElementBlock("p", null, [
          _createTextVNode(" Are you a member of a publishing collection society? ")
        ])), [
          [_directive_translate]
        ]),
        _createVNode(PushButtons, {
          modelValue: isMemberOfSociety.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isMemberOfSociety).value = $event)),
          disabled: hasIpiCaeNumber.value
        }, null, 8, ["modelValue", "disabled"]),
        (isMemberOfSociety.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(TextField, {
                name: "userDetails.ipiCaeNumber",
                disabled: hasIpiCaeNumber.value,
                label: _unref($gettext)('IPI/CAE number'),
                placeholder: _unref($gettext)('Enter your registered number'),
                "tooltip-info-text": _unref($gettext)('An IPI/CAE number is an international identification/membership number assigned to songwriters and publishers by collection societies to uniquely identify rights holders. Providing an IPI/CAE number can speed up the royalty collection process.')
              }, null, 8, ["disabled", "label", "placeholder", "tooltip-info-text"]),
              _createElementVNode("p", null, [
                _createElementVNode("span", null, _toDisplayString(_unref($gettext)('Are you affiliated to a mechanical rights organization or a performance rights organization or both?')), 1),
                _withDirectives(_createVNode(IconUI, {
                  class: "tooltip-icon",
                  "data-testid": "mro-pro-tooltip",
                  name: "tooltip",
                  fill: "info"
                }, null, 512), [
                  [_directive_tippy, { content: 'PRS, ASCAP, APRA ...' }]
                ])
              ]),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _withDirectives(_createElementVNode("input", {
                    id: "MechanicalRights",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isMechanicalRights).value = $event)),
                    type: "checkbox"
                  }, null, 512), [
                    [_vModelCheckbox, isMechanicalRights.value]
                  ]),
                  _createElementVNode("label", _hoisted_4, _toDisplayString(_unref($gettext)('Mechanical rights organization')), 1)
                ])
              ]),
              (isMechanicalRights.value && mechanicalRightsSocietiesOptions.value)
                ? (_openBlock(), _createBlock(DropdownUI, {
                    key: 0,
                    name: "userDetails.mro",
                    "toggle-text": _unref($gettext)('Select MRO...'),
                    options: mechanicalRightsSocietiesOptions.value
                  }, null, 8, ["toggle-text", "options"]))
                : _createCommentVNode("", true),
              (hasSelectedUnaffiliatedMRO.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref($gettext)('Ditto Music is not currently affiliated with your local collection society – You can still take advantage of our publishing service, but we would recommend that you continue submitting registrations to your local society so you can maximize your royalties. Ditto will then submit claims on your behalf to all major territories in our global publishing network.')), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _withDirectives(_createElementVNode("input", {
                    id: "PerformingRights",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isPerformingRights).value = $event)),
                    type: "checkbox"
                  }, null, 512), [
                    [_vModelCheckbox, isPerformingRights.value]
                  ]),
                  _createElementVNode("label", _hoisted_8, _toDisplayString(_unref($gettext)('Performance rights organization')), 1)
                ])
              ]),
              (isPerformingRights.value && performingRightsSocietiesOptions.value)
                ? (_openBlock(), _createBlock(DropdownUI, {
                    key: 2,
                    name: "userDetails.pro",
                    "toggle-text": _unref($gettext)('Select PRO...'),
                    options: performingRightsSocietiesOptions.value
                  }, null, 8, ["toggle-text", "options"]))
                : _createCommentVNode("", true),
              (hasSelectedUnaffiliatedPRO.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref($gettext)('Ditto Music is not currently affiliated with your local collection society – You can still take advantage of our publishing service, but we would recommend that you continue submitting registrations to your local society so you can maximize your royalties. Ditto will then submit claims on your behalf to all major territories in our global publishing network.')), 1))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_10, [
          _withDirectives((_openBlock(), _createElementBlock("button", {
            type: "button",
            class: "secondary full-width",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (hide()))
          }, [
            _createTextVNode(" Cancel ")
          ])), [
            [_directive_translate]
          ]),
          _withDirectives((_openBlock(), _createElementBlock("button", {
            type: "button",
            class: "full-width",
            disabled: !_unref(meta).valid,
            onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(onSubmit) && _unref(onSubmit)(...args)))
          }, [
            _createTextVNode(" Confirm ")
          ], 8, _hoisted_11)), [
            [_directive_translate]
          ])
        ])
      ], 544), [
        [_vShow, !isLoading.value]
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})